//HomePage.js

import { useEffect, useState } from 'react'
import { TypeAnimation } from 'react-type-animation'
import { Icon } from '@iconify/react'
import { motion } from 'framer-motion'
import BallCanvas from './technologies/TechnoBall'


const main_technologies = [
    {
        name: 'aws',
        icon: '/static/images/technos/aws.png',
    },
    {
        name: 'databricks',
        icon: '/static/images/technos/databricks.png',
    },
    {
        name: 'python',
        icon: '/static/images/technos/python.png',
    },
    {
        name: 'linux',
        icon: '/static/images/technos/linux.png',
    },
    {
        name: 'sql',
        icon: '/static/images/technos/sql.png',
    },
    
    {
        name: 'docker',
        icon: '/static/images/technos/docker.png',
    },
    {
        name: 'scikitlearn',
        icon: '/static/images/technos/scikitlearn.png',
    },
    {
        name: 'pytorch',
        icon: '/static/images/technos/pytorch.png',
    },
]

const mobile_web_technologies = [
    {
        name: 'cpp',
        icon: '/static/images/technos/cpp.png',
    },
    {
        name: 'rust',
        icon: '/static/images/technos/rust.png',
    },
    {
        name: 'html_css',
        icon: '/static/images/technos/html_css.png',
    },
    {
        name: 'react',
        icon: '/static/images/technos/react.png',
    },
    {
        name: 'reactnative',
        icon: '/static/images/technos/reactnative.png',
    },
    {
        name: 'kotlin',
        icon: '/static/images/technos/kotlin.png',
    },
    {
        name: 'onnx',
        icon: '/static/images/technos/onnx.png',
    },
    {
        name: 'opencv',
        icon: '/static/images/technos/opencv.png',
    },
]

const technoUp = {
    hidden: {
        y: '100vh',
        opacity: 0
    }
}

const technoDown = {
    hidden: {
        y: '-100vh',
        opacity: 0
    }
}

const technologiesVariant = {
    visible: {
        y: 0,
        opacity: 1,
        transition: {
            duration: 1.5,
            ease: 'easeInOut'
        }
    }
}

const technologyLabelMap = {
    scikitlearn: 'Sklearn',
    reactnative: 'ReactNative',
    html_css: 'Web',
    cpp: 'C++'
}

const getTechnologyLabel = (name) => technologyLabelMap[name] || name.charAt(0).toUpperCase() + name.slice(1)


const TypedTitle = ({ text, onComplete }) => (
    <h2 className="card-title justify-center text-center text-lg md:text-2xl font-semibold tracking-wide">
        <TypeAnimation
            sequence={[text, 1000, onComplete]}
            speed={85}
            style={{ fontSize: '1em' }}
            cursor={false}
        />
    </h2>
)

const SocialLinks = () => (
    <div className="flex flex-wrap justify-center gap-5">
        <SocialLink href="https://www.linkedin.com/in/alkai" icon="mdi:linkedin" />
        <SocialLink href="https://github.com/AlkaSaliss" icon="mdi:github" />
        <SocialLink href="https://stackoverflow.com/users/7437524/alka" icon="devicon-plain:stackoverflow-wordmark" />
    </div>
)

const SocialLink = ({ href, icon }) => (
    <a href={href} rel="noopener noreferrer" target='_blank'>
        <div className="grid w-20 h-20 md:w-24 md:h-24 rounded-[26px] place-items-center glass-panel glass-panel-hover border-2 border-primary/45 shadow-[0_0_32px_var(--neon-primary-soft)] hover:scale-110 hover:border-primary/70">
            <Icon icon={icon} className="text-[2.3rem] md:text-[2.7rem] text-base-content" />
        </div>
    </a>
)

const TechnologiesSection = ({ technologies, title, variants, className = 'w-full', useStaticIcons = false }) => (
    <div className={className}>
        <motion.div variants={{...variants, ...technologiesVariant}} initial="hidden" animate="visible">
            <div className="section-panel glass-panel glass-panel-strong">
                <div className="mb-4 flex items-center justify-between gap-3">
                    <div className="section-kicker">Toolkit</div>
                </div>
                <div className="mb-6 text-sm leading-6 text-base-content/75">
                    {title}
                </div>
                <div className="grid grid-cols-4 gap-3 place-items-start">
                    {technologies.map((technology, idx) => (
                        <div className="flex w-full flex-col items-center gap-2" key={`${technology.name}-${idx}`}>
                            <div className="w-16 h-16 md:w-20 md:h-20 rounded-[22px] glass-panel glass-panel-hover p-2">
                                {useStaticIcons ? (
                                    <img src={technology.icon} alt={technology.name} className="h-full w-full object-contain" />
                                ) : (
                                    <BallCanvas icon={technology.icon} />
                                )}
                            </div>
                            <div className="text-[10px] md:text-xs font-medium uppercase tracking-[0.08em] text-base-content/72 text-center">
                                {getTechnologyLabel(technology.name)}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </motion.div>
    </div>
)


const HomePage = () => {
    const [displayRole, setDisplayRole] = useState(false)
    const [displayTechnologies, setDisplayTechnologies] = useState(false)
    const [isMobile, setIsMobile] = useState(false)

    useEffect(() => {
        const updateViewport = () => setIsMobile(window.innerWidth < 768)
        updateViewport()
        window.addEventListener('resize', updateViewport)
        return () => window.removeEventListener('resize', updateViewport)
    }, [])

    return (
        <div className="homepage min-h-screen">
            <div className="page-frame flex flex-col justify-start items-center gap-6 min-h-screen pt-32 pb-10">
                <div className="w-full max-w-5xl section-panel glass-panel glass-panel-strong">
                    <div className="section-kicker mb-6">Open to freelance and permanent opportunities</div>
                    <div className="flex flex-col gap-8">
                        <div className="space-y-4">
                            <p className="text-sm uppercase tracking-[0.32em] text-base-content/50 text-center">Data Platforms · Data Engineering · ML Engineering</p>
                            <div className="space-y-3">
                                <TypedTitle text="Mahamadou ABOUBACAR" onComplete={() => setDisplayRole(true)} />
                                {displayRole && <TypedTitle text="Lead Data Engineer" onComplete={() => setDisplayTechnologies(true)} />}
                            </div>
                            <p className="mx-auto max-w-2xl text-center text-base leading-7 text-base-content/72">
                                I design and operate production-ready data & analytics platforms on Databricks and AWS. I help teams move from fragmented data sources to reliable ingestion, transformation, and self-service analytics workflows, while bridging data engineering with ML use cases.
                                I also deliver user enablement, support, and upskilling so data platforms drive adoption as well as technical delivery.
                            </p>
                        </div>

                        <div className="space-y-3">
                            <div className="text-center text-xs tracking-[0.3em] text-primary/70">Let's Connect</div>
                            <SocialLinks />
                        </div>
                    </div>
                </div>

                {displayTechnologies && (
                    <div className="grid w-full max-w-5xl gap-6 md:grid-cols-2">
                        <TechnologiesSection className="w-full" technologies={main_technologies} title="Core stack I use across analytics platforms, data engineering delivery, and ML enablement projects." variants={technoUp} useStaticIcons={isMobile} />
                        <TechnologiesSection className="w-full" technologies={mobile_web_technologies} title="Side-project stack for on-device ML, systems programming, mobile, and frontend experimentation." variants={technoDown} useStaticIcons={isMobile} />
                    </div>
                )}
            </div>
        </div>
    )
}

export default HomePage
